import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { NATIVE_AUTH_SCHEME } from '../lib/authRedirect'

export type BrokerSyncPortalClient = 'web' | 'native'
type BrokerSyncPortalResult = 'connected' | 'reconnected'
type BrokerSyncPortalStatus = 'SUCCESS' | 'ERROR' | 'ABANDONED'

type NativeBrokerSyncCallback = {
  portalResult: BrokerSyncPortalResult
  status: BrokerSyncPortalStatus | null
}

const NATIVE_BROKER_SYNC_HOST = 'broker-sync'
const SAFE_PORTAL_STATUSES = new Set<BrokerSyncPortalStatus>(['SUCCESS', 'ERROR', 'ABANDONED'])

export function getBrokerSyncPortalClient(): BrokerSyncPortalClient {
  return Capacitor.isNativePlatform() ? 'native' : 'web'
}

export function parseNativeBrokerSyncCallbackUrl(url: string): NativeBrokerSyncCallback | null {
  let parsed: URL

  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  if (parsed.protocol !== `${NATIVE_AUTH_SCHEME}:` || parsed.hostname !== NATIVE_BROKER_SYNC_HOST) {
    return null
  }

  const path = parsed.pathname.replace(/^\/+|\/+$/g, '')
  if (path !== 'connected' && path !== 'reconnected') {
    return null
  }

  const rawStatus = parsed.searchParams.get('status')?.toUpperCase() ?? null
  const status =
    rawStatus && SAFE_PORTAL_STATUSES.has(rawStatus as BrokerSyncPortalStatus)
      ? (rawStatus as BrokerSyncPortalStatus)
      : rawStatus
        ? 'ERROR'
        : null

  return {
    portalResult: path,
    status,
  }
}

async function closeBrokerSyncBrowser() {
  try {
    await Browser.close()
  } catch {
    // Browser.close is best-effort and is a no-op on Android.
  }
}

export async function handleNativeBrokerSyncCallbackUrl(url: string) {
  const callback = parseNativeBrokerSyncCallbackUrl(url)
  if (!callback) return false

  await closeBrokerSyncBrowser()

  const params = new URLSearchParams({
    tab: 'broker_sync',
    snaptrade: callback.portalResult,
  })
  if (callback.status) {
    params.set('status', callback.status)
  }

  window.history.replaceState(window.history.state, '', `/settings?${params.toString()}`)
  window.dispatchEvent(new Event('popstate'))
  return true
}

function assertSecurePortalUrl(url: string) {
  const parsed = new URL(url)
  if (parsed.protocol !== 'https:') {
    throw new Error('Broker connection portal must use HTTPS.')
  }
}

export async function openBrokerSyncPortal(url: string, onBrowserFinished?: () => void) {
  assertSecurePortalUrl(url)

  if (!Capacitor.isNativePlatform()) {
    window.location.href = url
    return
  }

  let finishedListener: PluginListenerHandle | undefined
  if (onBrowserFinished) {
    try {
      finishedListener = await Browser.addListener('browserFinished', () => {
        void finishedListener?.remove()
        onBrowserFinished()
      })
    } catch {
      // Opening the portal still works if the optional dismissal listener is unavailable.
    }
  }

  try {
    await Browser.open({ url })
  } catch (error) {
    await finishedListener?.remove()
    throw error
  }
}
